/**
 * Unified Theme Configuration
 * Centralized styling system for consistent UI across all components
 */

export const UNIFIED_THEME = {
  // Primary Colors
  primary: {
    main: "#2563eb",
    light: "#3b82f6",
    dark: "#1e40af",
    bg: "#dbeafe",
    text: "#0c4a6e",
  },

  // Secondary Colors
  secondary: {
    main: "#7c3aed",
    light: "#a78bfa",
    dark: "#6d28d9",
    bg: "#ede9fe",
  },

  // Status Colors
  success: {
    main: "#10b981",
    light: "#6ee7b7",
    dark: "#059669",
    bg: "#d1fae5",
  },
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
    bg: "#fef3c7",
  },
  error: {
    main: "#ef4444",
    light: "#fca5a5",
    dark: "#dc2626",
    bg: "#fee2e2",
  },
  info: {
    main: "#3b82f6",
    light: "#93c5fd",
    dark: "#1d4ed8",
    bg: "#eff6ff",
  },

  // Neutral Colors
  gray: {
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca3af",
    500: "#6b7280",
    600: "#4b5563",
    700: "#374151",
    800: "#1f2937",
    900: "#111827",
  },

  // Backgrounds
  background: "#f8fafc",
  surface: "#ffffff",
  surfaceAlt: "#f3f4f6",

  // Borders
  border: {
    light: "#e5e7eb",
    main: "#d1d5db",
    dark: "#9ca3af",
    radius: "8px",
    radiusSm: "6px",
    radiusLg: "12px",
  },

  // Shadows
  shadow: {
    xs: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    sm: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },

  // Animations
  animation: {
    fast: "0.15s",
    normal: "0.3s",
    slow: "0.5s",
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
    easeOut: "cubic-bezier(0, 0, 0.2, 1)",
  },

  // Spacing
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
  },

  // Typography
  typography: {
    h1: {
      fontSize: "2rem",
      fontWeight: "700",
      lineHeight: "1.2",
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: "700",
      lineHeight: "1.3",
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: "600",
      lineHeight: "1.4",
    },
    body: {
      fontSize: "1rem",
      fontWeight: "400",
      lineHeight: "1.5",
    },
    bodySmall: {
      fontSize: "0.875rem",
      fontWeight: "400",
      lineHeight: "1.25",
    },
    label: {
      fontSize: "0.875rem",
      fontWeight: "600",
      lineHeight: "1.25",
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: "500",
      lineHeight: "1",
    },
  },
};

// Button Styles
export const BUTTON_STYLES = {
  // Primary Button
  primary: `
    bg-blue-600 hover:bg-blue-700 text-white
    px-4 py-2 rounded-lg font-medium text-sm
    transition-all duration-200
    active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-400/50
    shadow-sm hover:shadow-md
    flex items-center gap-2 justify-center
  `,

  // Secondary Button
  secondary: `
    bg-gray-200 hover:bg-gray-300 text-gray-800
    px-4 py-2 rounded-lg font-medium text-sm
    transition-all duration-200
    active:scale-95 focus:outline-none focus:ring-2 focus:ring-gray-400/50
  `,

  // Danger Button
  danger: `
    bg-red-600 hover:bg-red-700 text-white
    px-4 py-2 rounded-lg font-medium text-sm
    transition-all duration-200
    active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-400/50
    shadow-sm hover:shadow-md
  `,

  // Success Button
  success: `
    bg-green-600 hover:bg-green-700 text-white
    px-4 py-2 rounded-lg font-medium text-sm
    transition-all duration-200
    active:scale-95 focus:outline-none focus:ring-2 focus:ring-green-400/50
    shadow-sm hover:shadow-md
  `,

  // Ghost Button
  ghost: `
    bg-transparent hover:bg-gray-100 text-gray-700
    px-4 py-2 rounded-lg font-medium text-sm
    transition-all duration-200
    active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-400/50
    border border-gray-200 hover:border-gray-300
  `,

  // Icon Button
  icon: `
    p-2 rounded-lg hover:bg-gray-100
    text-gray-600 hover:text-gray-900
    transition-all duration-200
    active:scale-90 focus:outline-none focus:ring-2 focus:ring-blue-400/50
  `,

  // Small Button
  small: `
    px-3 py-1.5 rounded-md font-medium text-xs
    transition-all duration-200
    active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-400/50
  `,
};

// Input Styles
export const INPUT_STYLES = {
  base: `
    w-full px-3 py-2 rounded-lg
    border border-gray-300 focus:border-blue-500
    focus:outline-none focus:ring-2 focus:ring-blue-400/30
    transition-all duration-200
    placeholder-gray-400
    text-gray-900
  `,
  error: `
    border-red-500 focus:border-red-600 focus:ring-red-400/30
  `,
  success: `
    border-green-500 focus:border-green-600 focus:ring-green-400/30
  `,
};

// Badge/Pill Styles
export const BADGE_STYLES = {
  primary: "bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium",
  success: "bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium",
  warning: "bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-medium",
  error: "bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-medium",
  gray: "bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-xs font-medium",
};

// List Item Styles
export const LIST_ITEM_STYLES = {
  container: `
    bg-white border border-gray-200 rounded-lg
    hover:shadow-md hover:border-gray-300
    transition-all duration-200
    p-4 flex items-center gap-4
  `,
  avatar: `
    w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600
    flex items-center justify-center text-white font-bold text-sm
    flex-shrink-0
  `,
  content: `
    flex-1 min-w-0
  `,
  title: `
    font-semibold text-gray-900 truncate
  `,
  subtitle: `
    text-sm text-gray-500 truncate
  `,
  actions: `
    flex items-center gap-2
  `,
};

// Modal Styles
export const MODAL_STYLES = {
  overlay: `
    fixed inset-0 bg-black/30 backdrop-blur-sm z-50
    flex items-center justify-center p-4
  `,
  content: `
    bg-white rounded-lg shadow-xl max-w-sm w-full
    border border-gray-200
  `,
  header: `
    px-6 py-4 border-b border-gray-200
    flex items-center justify-between
  `,
  body: `
    px-6 py-4
  `,
  footer: `
    px-6 py-4 border-t border-gray-200
    flex items-center justify-end gap-2
  `,
};

// Toast Styles
export const TOAST_STYLES = {
  success: `
    bg-green-50 border border-green-300
    text-green-700 rounded-lg p-4
  `,
  error: `
    bg-red-50 border border-red-300
    text-red-700 rounded-lg p-4
  `,
  warning: `
    bg-yellow-50 border border-yellow-300
    text-yellow-700 rounded-lg p-4
  `,
  info: `
    bg-blue-50 border border-blue-300
    text-blue-700 rounded-lg p-4
  `,
};

// Scrollbar Styles (CSS)
export const SCROLLBAR_CSS = `
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
    border: 2px solid #f1f5f9;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;
